import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { AlertTriangle, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

/**
 * Shows a persistent banner when the admin's nursery is missing
 * required profile data (address / phone). Cannot be dismissed —
 * it disappears only once the data is complete.
 */
const NurseryProfileCompletionBanner = () => {
  const { profile, roles } = useAuth();
  const nurseryId = profile?.nursery_id;
  const isAdmin = roles.includes("admin");

  const { data } = useQuery({
    queryKey: ["nursery-profile-completion", nurseryId],
    enabled: !!nurseryId && isAdmin,
    refetchInterval: 60_000,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("nurseries")
        .select("name, address, phone")
        .eq("id", nurseryId!)
        .single();
      if (error) throw error;
      return data;
    },
  });

  if (!isAdmin || !data) return null;

  const missing: string[] = [];
  if (!data.address || !data.address.trim()) missing.push("العنوان");
  if (!data.phone || !data.phone.trim()) missing.push("رقم الهاتف");

  if (missing.length === 0) return null;

  return (
    <div
      className="rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-800 p-3 mb-4 flex items-start gap-3"
      dir="rtl"
    >
      <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-amber-900 dark:text-amber-200">
          إكمال بيانات الحضانة مطلوب
        </p>
        <p className="text-xs text-amber-800/80 dark:text-amber-200/70 mt-1">
          البيانات الناقصة: {missing.join("، ")}. يرجى إكمالها من الإعدادات.
        </p>
      </div>
      <Button asChild size="sm" variant="outline" className="h-7 text-xs gap-1 border-amber-400 flex-shrink-0">
        <Link to="/dashboard/settings">
          <ExternalLink className="w-3 h-3" />
          فتح الإعدادات
        </Link>
      </Button>
    </div>
  );
};

export default NurseryProfileCompletionBanner;
